import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { observer } from 'mobx-react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { Grid, Tabs } from '@material-ui/core';

import { brandLogo, brandConstants } from 'brand-config';
import { useStore } from 'core/store';
import GlobalResults from 'common/components/search-results/global-results';
import { buildGlobalSearchHits } from 'common/components/search-results/global-results/global-search-hits';
import useSession from 'core/hooks/use-session';
import { isCanonicalUser } from 'core/helpers/troubleshooting-mode';
import AccountMenu from '../account-menu';
import plus from 'core/assets/svg/add-2.svg';
import portalOrange from 'core/assets/svg/portal-orange.svg';
import search from 'core/assets/svg/search.svg';
import { HeaderTab } from '../header-tab';
import { SearchBar } from '../search-bar';

import useStyles from './header.styles';

// The bar's promise has to match what it actually searches: agents read it literally, and the
// members-only wording is what made a nameless offer search look like the offer didn't exist
// (#35516). Agents without offer access still get the members-only wording.
const MEMBER_SEARCH_PLACEHOLDER = 'Search for a member by account ID, name, or address';
const MEMBER_AND_OFFER_SEARCH_PLACEHOLDER = 'Search for a member or offer by account ID, name, or address';

const Header = observer(() => {
  const classes = useStyles({ brandName: brandConstants.SHORT_NAME.toLowerCase() });
  const session = useSession();
  const history = useHistory();
  const [dropdownVisible, setDropdownVisible] = useState(true);

  const onSearchPage = !!useRouteMatch({ path: '/search' });
  const onQuotePage = !!useRouteMatch({ path: '/quote' });
  const onPortalPage = !!useRouteMatch({ path: '/portal' });

  const makeActiveTabs = () => {
    if (session.showAgencyPortal && session.viewOnly) {
      return {
        PORTAL: 0,
        SEARCH: 1,
        NONE: false
      };
    }
    if (session.showAgencyPortal) {
      return {
        PORTAL: 0,
        NEW_QUOTE: 1,
        SEARCH: 2,
        //  when no tab is selected, materialui wont throw a warning if we send in a value of false
        // https://stackoverflow.com/questions/50496166/material-ui-tabs-no-selected-tab
        NONE: false
      };
    }
    return {
      NEW_QUOTE: 0,
      SEARCH: 1,
      NONE: false
    };
  };

  const TABS = makeActiveTabs();

  const activeTab =
    onPortalPage && session.showAgencyPortal
      ? TABS.PORTAL
      : onQuotePage && session.canQuote
        ? TABS.NEW_QUOTE
        : onSearchPage
          ? TABS.SEARCH
          : TABS.NONE;

  const {
    search: {
      searchString,
      loading,
      customersResults,
      offersResults,
      clearInput,
      searchCustomersAction,
      searchOffersAction,
      setSearchString
    },
    affinityLookups,
    coBranding
  } = useStore();

  // Mirror the Search page's Offers tab exactly (search-header.jsx) — an agent who can't open
  // that tab shouldn't reach offers through the header either. `isCanonicalUser` is the same
  // troubleshooting-offer exclusion the Search page applies (#30994).
  const canSearchOffers = (session?.canEdit && session?.canQuote) || Boolean(session?.viewOnly);
  const excludeTroubleshootingOffers = isCanonicalUser(session);
  const searchPlaceholder = canSearchOffers ? MEMBER_AND_OFFER_SEARCH_PLACEHOLDER : MEMBER_SEARCH_PLACEHOLDER;

  // One ranked list drawn from two indexes, so each row has to say which it is (#35516). Offer
  // hits are dropped outright when the agent can't see offers — `offersResults` is shared with
  // the Search page and can hold results from an earlier, permitted search.
  const globalSearchHits = useMemo(
    () =>
      buildGlobalSearchHits({
        customerHits: customersResults.hits,
        offerHits: canSearchOffers ? offersResults.hits : []
      }),
    [customersResults.hits, offersResults.hits, canSearchOffers]
  );

  const affinityCode = session?.user?.affinityCode;

  useEffect(() => {
    coBranding.fetchPartnerAssets(affinityCode);
  }, [coBranding, affinityCode]);

  const partnerAssets = coBranding.partnerAssets;
  const showPartner = !!(partnerAssets?.includeInHeader && partnerAssets.imgSrc);
  // `branchLogoIncluded` assets are complete lockups (Branch already embedded) — render as-is, don't compose one.
  const partnerLogoIsSelfContainedLockup = showPartner && !!partnerAssets.branchLogoIncluded;

  const routes = useMemo(() => {
    if (!session.canQuote && !session.showAgencyPortal) {
      return ['/search/offers'];
    }
    if (!session.showAgencyPortal) {
      return ['/quote', '/search/offers'];
    }
    if (!session.canQuote) {
      return ['/portal/updates', '/search/offers'];
    }
    return ['/portal/updates', '/quote', '/search/offers'];
  }, [session]);

  const handleTabChange = useCallback(
    (e, newTab) => {
      e.preventDefault();
      if (activeTab === newTab) {
        // force reload the page because user is trying to navigate to the same tab they are already viewing
        // https://stackoverflow.com/questions/46820682/how-do-i-reload-a-page-with-react-router
        history.go(0);
      }
      history.push(routes[newTab]);
    },
    [history, routes, activeTab]
  );

  const hideOrShowDropdown = useCallback(
    (value) => {
      setDropdownVisible(value);
    },
    [setDropdownVisible]
  );

  const handleClickAway = useCallback(() => {
    hideOrShowDropdown(false);
  }, [hideOrShowDropdown]);

  // Each cell is a link, so the click that opened the row has already navigated; this only
  // dismisses the dropdown behind it.
  const handleRowClick = useCallback(() => {
    hideOrShowDropdown(false);
  }, [hideOrShowDropdown]);

  const clearSearchInput = useCallback(() => {
    clearInput();
  }, [clearInput]);

  const onSearchInputFocus = useCallback(() => {
    hideOrShowDropdown(true);
  }, [hideOrShowDropdown]);

  const onSearchStringChange = useCallback(
    (event) => {
      setSearchString(event.target.value);
      searchCustomersAction();
      if (canSearchOffers) {
        // Always collapse an offer's revisions here: this is a quick-jump list, and the Search
        // page's "Show Offer History" checkbox is sticky store state the agent isn't thinking
        // about while typing in the toolbar.
        searchOffersAction({ excludeTroubleshootingOffers, showOfferHistory: false });
      }
    },
    [setSearchString, searchCustomersAction, searchOffersAction, canSearchOffers, excludeTroubleshootingOffers]
  );

  if (!session) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <AppBar position="absolute" color="inherit" className={classes.header} elevation={1}>
          <Toolbar className={classes.toolbar} />
        </AppBar>
      </ClickAwayListener>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <AppBar
        position="relative"
        color="inherit"
        className={classNames(classes.header, { [classes.headerFlexible]: partnerLogoIsSelfContainedLockup })}
        elevation={1}
      >
        <Toolbar
          className={classNames(classes.toolbar, { [classes.toolbarFlexible]: partnerLogoIsSelfContainedLockup })}
        >
          <Grid container spacing={3} alignItems="center" justifyContent="space-between" wrap="nowrap" direction="row">
            <div className={classes.logoLockup}>
              {partnerLogoIsSelfContainedLockup ? (
                <Link to="/" data-cy="partner-logo">
                  <img
                    src={partnerAssets.imgSrc}
                    alt={partnerAssets.imgAlt || partnerAssets.name || 'Partner'}
                    className={classes.partnerLogoSelfContained}
                  />
                </Link>
              ) : (
                <>
                  <Link to="/">
                    <img src={brandLogo} alt="gobranch" className={classes.logo} data-cy="logo" />
                  </Link>
                  {showPartner && (
                    <div className={classes.partnerLogoContainer} data-cy="partner-logo">
                      <img
                        src={partnerAssets.imgSrc}
                        alt={partnerAssets.imgAlt || partnerAssets.name || 'Partner'}
                        className={classes.partnerLogo}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {session.isLoggedIn && !affinityLookups.loading && (
              <>
                <Grid item xs={5} className={classes.searchContainer}>
                  {!onSearchPage && (
                    <SearchBar
                      hideOrShowDropdown={hideOrShowDropdown}
                      onCloseIconClick={clearSearchInput}
                      onInputFocus={onSearchInputFocus}
                      onInputChange={onSearchStringChange}
                      placeholder={searchPlaceholder}
                      value={searchString}
                    />
                  )}
                </Grid>
                <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="secondary" variant="fullWidth">
                  {session.showAgencyPortal && <HeaderTab label="Portal" iconSrc={portalOrange} />}
                  {session.canQuote && <HeaderTab label="New Offer" iconSrc={plus} iconSize="large" />}
                  <HeaderTab label="Search" iconSrc={search} />
                </Tabs>
                <AccountMenu />
              </>
            )}
          </Grid>
        </Toolbar>
        {!onSearchPage && searchString && dropdownVisible && (
          <GlobalResults loading={loading} hits={globalSearchHits} onRowClick={handleRowClick} />
        )}
      </AppBar>
    </ClickAwayListener>
  );
});

Header.propTypes = {
  classes: PropTypes.object.isRequired
};

export default Header;
